/** Demo Mode only - Finance > EBIT Analysis tab. EBIT trend (monthly/cumulative/margin),
 * an EBIT bridge reconciling Budget EBIT -> Current forecast EBIT, and contribution
 * analysis by client/sector/region/month. The bridge is deliberately built from the
 * dataset's actual granularity (revenue/cost of sales/labour/opex/overhead) rather than
 * inventing sub-categories (e.g. price vs volume) the mock dataset doesn't model - see
 * financeData.ts header for the full list of modelling assumptions. */

import { useEffect, useMemo } from 'react';
import { Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Section } from '../../../components/ui/Section';
import { applyRoundAndFormat } from '../../../utils';
import useCurrencyCodeConfiguration from '../../../hooks/configurations/useCurrencyCodeConfiguration';
import { companyMonthlyTrend, computeTotals, sumLineItems } from '../../../mocks/data/financeData';
import { useFinanceFilters } from '../FinanceFiltersContext';
import TrendChart from '../components/TrendChart';
import EbitBridgeChart from '../components/EbitBridgeChart';

const EbitAnalysisTab = () => {
  const { t } = useTranslation('common');
  const { currencyCode, getCurrencyCodeConfiguration } = useCurrencyCodeConfiguration();
  const { scopedContracts } = useFinanceFilters();

  useEffect(() => {
    getCurrencyCodeConfiguration();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formatCurrency = (value: number) => (value < 0 ? `(${applyRoundAndFormat(Math.abs(value), currencyCode)})` : applyRoundAndFormat(value, currencyCode));

  const budget = computeTotals(sumLineItems(scopedContracts.map((c) => c.scenarios.budget)));
  const forecast = computeTotals(sumLineItems(scopedContracts.map((c) => c.scenarios.currentForecast)));

  const trendForecast = companyMonthlyTrend('currentForecast');
  const trendBudget = companyMonthlyTrend('budget');
  const trendPriorYear = companyMonthlyTrend('priorYear');

  const bridgeSteps = useMemo(() => {
    const revenueVariance = forecast.totalRevenue - budget.totalRevenue;
    const costOfSalesVariance = -(forecast.totalCostOfSales - budget.totalCostOfSales);
    const labourVariance = -(forecast.totalLabour - budget.totalLabour);
    const opexVariance = -(forecast.totalOpex - budget.totalOpex);
    const overheadVariance = -(forecast.allocatedOverhead - budget.allocatedOverhead);
    return [
      { label: t('financeEbitBridgeBudget', 'Budget EBIT'), value: budget.ebit, isTotal: true },
      { label: t('financeEbitBridgeRevenue', 'Revenue variance'), value: revenueVariance },
      { label: t('financeEbitBridgeCostOfSales', 'Cost of sales variance'), value: costOfSalesVariance },
      { label: t('financeEbitBridgeLabour', 'Labour variance'), value: labourVariance },
      { label: t('financeEbitBridgeOpex', 'Operating expenses variance'), value: opexVariance },
      { label: t('financeEbitBridgeOverhead', 'Overhead variance'), value: overheadVariance },
      { label: t('financeEbitBridgeForecast', 'Current forecast EBIT'), value: forecast.ebit, isTotal: true },
    ];
  }, [budget, forecast, t]);

  const contribution = useMemo(() => scopedContracts
    .map((c) => {
      const cForecast = computeTotals(c.scenarios.currentForecast);
      const cBudget = computeTotals(c.scenarios.budget);
      return {
        contract: c, ebit: cForecast.ebit, ebitMargin: cForecast.ebitMarginPercentage, deterioration: cForecast.ebit - cBudget.ebit,
      };
    })
    .sort((a, b) => a.deterioration - b.deterioration), [scopedContracts]);

  return (
    <Box>
      <Section style={{ p: 4, mb: 6 }}>
        <TrendChart
          title={t('financeEbitTrendTitle', 'EBIT trend - actual/forecast vs budget vs prior year')}
          series={[
            { label: t('financePnlCurrentForecast', 'Actual + current forecast'), data: trendForecast.ebit, color: '#1976d2' },
            { label: t('financePnlBudget', 'Budget'), data: trendBudget.ebit, color: '#9e9e9e', dashed: true },
            { label: t('financePnlPriorYear', 'Prior year'), data: trendPriorYear.ebit, color: '#9c27b0' },
          ]}
          valueFormatter={(v) => (v === null ? '' : applyRoundAndFormat(v, currencyCode))}
        />
      </Section>

      <Section style={{ p: 4, mb: 6 }}>
        <EbitBridgeChart title={t('financeEbitBridgeTitle', 'EBIT bridge - budget to current forecast')}
          steps={bridgeSteps}
          currencyCode={currencyCode} />
      </Section>

      <Section style={{ p: 4 }}>
        <Typography variant="textMD"
          fontWeight={600}
          sx={{ mb: 3 }}>
          {t('financeEbitContributionTitle', 'EBIT contribution by contract')}
        </Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('commercialPerformanceCustomer', 'Client')}</TableCell>
                <TableCell>{t('financeSector', 'Sector')}</TableCell>
                <TableCell align="right">{t('financePnlEbit', 'EBIT')}</TableCell>
                <TableCell align="right">{t('financePnlEbitMargin', 'EBIT margin')}</TableCell>
                <TableCell align="right">{t('financeEbitForecastMovement', 'Forecast movement vs budget')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {contribution.map(({ contract, ebit, ebitMargin, deterioration }) => (
                <TableRow key={contract.id}>
                  <TableCell>{contract.client}</TableCell>
                  <TableCell>{contract.sector}</TableCell>
                  <TableCell align="right">{formatCurrency(ebit)}</TableCell>
                  <TableCell align="right">{`${ebitMargin.toFixed(1)}%`}</TableCell>
                  <TableCell align="right"
                    sx={{ color: deterioration < 0 ? 'error.main' : 'success.main' }}>
                    {`${deterioration < 0 ? '(' : '+'}${applyRoundAndFormat(Math.abs(deterioration), currencyCode)}${deterioration < 0 ? ')' : ''}`}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Section>
    </Box>
  );
};

export default EbitAnalysisTab;
